/**
 * The lines-remaining map (Project reporting spec §4.2.1 "Lines-remaining map", MB decision 29; docs/specs/map.md
 * §5): one layer per block drawn from `planProgress(...).lines` and the plan.
 *
 * - Each original line still remaining is drawn once over its full geometry, coloured by its state with the line
 *   state tokens of decision 372 (map colours of their own, decision 352), in the line states' words on every map
 *   (decision 659): Unflown in `map-line-unflown`, thin and dashed; Flown, awaiting QC `map-line-awaiting-qc`;
 *   rejected with no reflight planned `map-line-rejected` (red); reflight planned `map-line-reflight` (yellow); part
 *   accepted with no patch `map-line-part-accepted` (purple). Every state has a colour of its own (MB, 25 Oct 2026:
 *   no more two reds and two pinks). Every line has the thin dark edge under it (decision 654).
 * - A Completed line is not drawn (MB's R136, 2 Nov 2026: the map showed completed lines in green; it shows only the
 *   lines remaining). "Flown, awaiting QC" lines are drawn: they are not completed until QC passes them. The Completed
 *   colour and word stay on the Line states layer and in the Remaining lines table (decisions 659 and 735);
 *   `REMAINING_LEGEND` keeps every state for the table, `REMAINING_MAP_LEGEND` lists what the map draws.
 * - A reflight is not drawn as a separate line: the original's yellow carries "<number> reflight" at its start.
 * - A patch is drawn over its segment only: the segment `map-line-to-fly` (pink) and thick, labelled "<number> patch,
 *   4.15 km"; the rest of the original is completed and so not drawn. A flown patch awaiting review is
 *   `map-line-awaiting-qc` over the segment.
 * - A patch still to fly and a planned reflight name the way they are planned to be flown (`direction_deg`, the Refly
 *   list's direction); with `arrows` the map draws arrows along them (decision 1011, `direction-arrows.ts`): Teslyn
 *   Field's map, the brief's map and Teslyn Cloud's staff map, never the client portal's.
 * - Logged coverage (a toggle) is drawn from `start_along_m`/`end_along_m` as a thin line beside the planned line.
 * - Every colour is paired with a word in the legend and the tooltip. The header and each block's legend read
 *   "Remaining 1,234.56 km of 25,493.93 km planned", the Job Summary's "Est KM Remaining".
 */
import { formatKm } from './km.js';
import type { Block, LineStatus, PlannedLine, Project } from './model.js';
import {
  blockMap,
  plannedLinePoints,
  plannedSegmentPoints,
  type LegendEntry,
  type MapFeature,
  type MapStroke,
  type MapColour,
} from './map-layers.js';
import { planProgress, type LineProgress, type LineState } from './progress.js';
import { plannedLineRole } from './reflights.js';
import {
  alongDirection,
  directionArrowFeatures,
  plannedDirection,
  type PlannedDirection,
} from './direction-arrows.js';

/**
 * The legend of the lines-remaining map, in the order of the design note, in the line states' words (decision 659:
 * "Unflown", "Flown, awaiting QC" and "Completed" on every map, no longer "Not flown", "Awaiting review" and
 * "Accepted").
 */
export const REMAINING_LEGEND: Readonly<Record<LineState, LegendEntry>> = {
  accepted: { colour: 'map-line-completed', stroke: 'thin', dashed: false, word: 'Completed' },
  awaiting_review: {
    colour: 'map-line-awaiting-qc',
    stroke: 'normal',
    dashed: false,
    word: 'Flown, awaiting QC',
  },
  reflight_planned: {
    colour: 'map-line-reflight',
    stroke: 'normal',
    dashed: false,
    word: 'Reflight planned',
  },
  patch_planned: { colour: 'map-line-to-fly', stroke: 'thick', dashed: false, word: 'Patch planned' },
  rejected: {
    colour: 'map-line-rejected',
    stroke: 'normal',
    dashed: false,
    word: 'Rejected, no reflight planned',
  },
  partial: {
    colour: 'map-line-part-accepted',
    stroke: 'normal',
    dashed: false,
    word: 'Part accepted, no patch planned',
  },
  not_flown: { colour: 'map-line-unflown', stroke: 'thin', dashed: true, word: 'Unflown' },
};

/**
 * The states the lines-remaining map draws (R136): every line still to fly or still to pass QC, in the legend's
 * order. A Completed line is not remaining and is not drawn.
 */
export const REMAINING_MAP_STATES: readonly LineState[] = [
  'not_flown',
  'awaiting_review',
  'reflight_planned',
  'patch_planned',
  'rejected',
  'partial',
];

/** The lines-remaining map's legend: only the states it draws (R136). */
export const REMAINING_MAP_LEGEND: readonly LegendEntry[] = REMAINING_MAP_STATES.map(
  (s) => REMAINING_LEGEND[s],
);

/** The logged coverage swatch (the "Logged coverage" toggle). */
export const COVERAGE_LEGEND: LegendEntry = {
  colour: 'map-line-coverage',
  stroke: 'thin',
  dashed: false,
  word: 'Logged coverage',
};

/** Metres beside the planned line at which logged coverage is drawn. */
export const COVERAGE_OFFSET_M = 40;

export const remainingText = (remainingM: number, plannedM: number): string =>
  `Remaining ${formatKm(remainingM / 1000)} km of ${formatKm(plannedM / 1000)} km planned`;

export interface BlockRemaining {
  readonly block: Block;
  readonly remainingM: number;
  readonly plannedM: number;
  /** "Remaining … km of … km planned". */
  readonly text: string;
  /** Lines per state, for the block's legend. */
  readonly counts: Readonly<Record<LineState, number>>;
  readonly patches: number;
}

export interface LinesRemaining {
  readonly remainingM: number;
  readonly plannedM: number;
  readonly text: string;
  readonly blocks: readonly BlockRemaining[];
  readonly features: readonly MapFeature[];
  readonly notDrawn: readonly string[];
}

const zeroCounts = (): Record<LineState, number> => ({
  not_flown: 0,
  awaiting_review: 0,
  accepted: 0,
  reflight_planned: 0,
  patch_planned: 0,
  rejected: 0,
  partial: 0,
});

const km = (m: number): string => `${formatKm(m / 1000)} km`;

/** A line's two points the other way round when its own geometry runs against the original's. */
const orient = <T>(pts: [T, T] | null, reversed: boolean): [T, T] | null =>
  pts && reversed ? [pts[1], pts[0]] : pts;

function feature(
  id: string,
  points: MapFeature['geometry'] & { type: 'line' },
  colour: MapColour,
  stroke: MapStroke,
  dashed: boolean,
  tooltip: string,
  identify: MapFeature['identify'],
  label?: string,
  direction?: PlannedDirection,
): MapFeature {
  // the points run the way the line is to be flown; the label stays at the original's first point
  const labelAt = direction?.againstPlan ? points.points[points.points.length - 1]! : points.points[0]!;
  return {
    id,
    layer: 'remaining',
    geometry: points,
    style: { colour, stroke, dashed, fill: 0, casing: true },
    tooltip,
    identify,
    ...(label ? { label, label_at: labelAt } : {}),
    ...(direction ? { direction_deg: direction.deg, direction_from: direction.from } : {}),
  };
}

/**
 * Remaining km by block and the lines-remaining features, from the plan and reviewed line status. `coverage` adds
 * the logged coverage of each status that has `start_along_m`/`end_along_m`. `completed` draws the Completed lines
 * too, in `map-line-completed` (a flight's own lines on Teslyn Cloud, `flightMap`: what the flight flew, not what
 * remains); the lines-remaining map leaves them out (R136). `arrows` adds the direction arrows of the patches and
 * reflights still to fly (decision 1011): not on the client portal's map.
 */
export function linesRemaining(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  project: Pick<Project, 'epsg'>,
  options: {
    readonly coverage?: boolean;
    readonly completed?: boolean;
    readonly arrows?: boolean;
  } = {},
): LinesRemaining {
  const progress = planProgress(statuses, plan);
  const live = statuses.filter((s) => s.deleted_at === null);
  const bm = blockMap(blocks);
  const features: MapFeature[] = [];
  let missing = 0;
  const byBlock = new Map<string, LineProgress[]>();
  for (const lp of progress.lines) {
    byBlock.set(lp.original.block_id, [...(byBlock.get(lp.original.block_id) ?? []), lp]);
  }

  for (const lp of progress.lines) {
    // a Completed line is not remaining: not drawn (R136), unless every line flown is asked for
    if (lp.state === 'accepted' && !options.completed) continue;
    const o = lp.original;
    const pts = plannedLinePoints(o, bm, project);
    if (!pts) {
      missing += 1;
      continue;
    }
    const geom = { type: 'line' as const, points: pts };
    const legend = REMAINING_LEGEND[lp.state];
    const remainingRow = { label: 'Remaining', value: km(lp.remaining_m) };
    const base = [
      { label: 'Line', value: String(o.number) },
      { label: 'Kind', value: o.kind === 'tie' ? 'Tie' : 'Traverse' },
      { label: 'Planned', value: km(o.length_m) },
    ];
    const open = lp.open;
    const openIsPatch = open !== null && plannedLineRole(open) === 'patch';
    if (openIsPatch && (lp.state === 'patch_planned' || lp.state === 'awaiting_review')) {
      // the patch's segment only: the original is completed outside it and so not drawn (R136), unless every line
      // flown is asked for
      if (options.completed) {
        const acc = REMAINING_LEGEND.accepted;
        features.push(
          feature(
            o.id,
            geom,
            acc.colour,
            acc.stroke,
            acc.dashed,
            `${o.number} · Completed outside the patch · Remaining ${km(lp.remaining_m)}`,
            [...base, { label: 'State', value: 'Completed outside the patch' }, remainingRow],
          ),
        );
      }
      const dir = plannedDirection(o, open, live);
      const flownPatch = lp.state === 'awaiting_review';
      const inOrder =
        open.segment_from_m != null && open.segment_to_m != null
          ? plannedSegmentPoints(o, open.segment_from_m, open.segment_to_m, bm, project)
          : orient(plannedLinePoints(open, bm, project), dir.reversed);
      // a patch still to fly runs the way the original was flown (decision 1013)
      const seg = flownPatch ? inOrder : alongDirection(inOrder, dir);
      if (seg) {
        const word = flownPatch ? 'Patch flown, awaiting QC' : 'Patch planned';
        const patchLabel = `${open.number} patch, ${km(open.length_m)}`;
        features.push(
          feature(
            open.id,
            { type: 'line', points: seg },
            flownPatch ? 'map-line-awaiting-qc' : 'map-line-to-fly',
            'thick',
            false,
            `${patchLabel} · ${word}`,
            [
              { label: 'Patch', value: String(open.number) },
              { label: 'State', value: word },
              {
                label: 'Segment',
                value: `${formatKm((open.segment_from_m ?? 0) / 1000)} to ${formatKm((open.segment_to_m ?? 0) / 1000)} km along ${o.number}`,
              },
              { label: 'Length', value: km(open.length_m) },
              remainingRow,
            ],
            patchLabel,
            flownPatch ? undefined : dir,
          ),
        );
      } else missing += 1;
    } else {
      const reflightLabel =
        lp.state === 'reflight_planned' && open !== null ? `${open.number} reflight` : undefined;
      const dir = reflightLabel ? plannedDirection(o, open, live) : undefined;
      features.push(
        feature(
          o.id,
          dir ? { type: 'line', points: alongDirection(pts, dir)! } : geom,
          legend.colour,
          legend.stroke,
          legend.dashed,
          `${o.number} · ${legend.word}${reflightLabel ? ` (${reflightLabel})` : ''} · Remaining ${km(lp.remaining_m)}`,
          [
            ...base,
            { label: 'State', value: legend.word },
            ...(reflightLabel ? [{ label: 'Reflight', value: String(open!.number) }] : []),
            remainingRow,
          ],
          reflightLabel,
          dir,
        ),
      );
    }
  }

  if (options.coverage) {
    const byNumber = new Map(plan.filter((p) => p.deleted_at === null).map((p) => [p.number, p]));
    const byId = new Map(plan.map((p) => [p.id, p]));
    for (const s of statuses.filter((x) => x.deleted_at === null)) {
      if (s.start_along_m == null || s.end_along_m == null) continue;
      const line = byNumber.get(s.line_number) ?? byId.get(s.planned_line_id);
      if (!line) continue;
      const original = line.reflight_of_line_id ? (byId.get(line.reflight_of_line_id) ?? line) : line;
      const seg = plannedSegmentPoints(
        original,
        s.start_along_m,
        s.end_along_m,
        bm,
        project,
        COVERAGE_OFFSET_M,
      );
      if (!seg) continue;
      features.push(
        feature(
          `coverage:${s.id}`,
          { type: 'line', points: seg },
          COVERAGE_LEGEND.colour,
          COVERAGE_LEGEND.stroke,
          false,
          `${s.line_number} · Logged coverage · ${km(Math.abs(s.end_along_m - s.start_along_m))}`,
          [
            { label: 'Line', value: String(s.line_number) },
            { label: 'Logged from', value: km(Math.min(s.start_along_m, s.end_along_m)) },
            { label: 'Logged to', value: km(Math.max(s.start_along_m, s.end_along_m)) },
          ],
        ),
      );
    }
  }

  const blockRows: BlockRemaining[] = blocks
    .filter((b) => b.deleted_at === null && byBlock.has(b.id))
    .map((block) => {
      const lines = byBlock.get(block.id) ?? [];
      const counts = zeroCounts();
      for (const l of lines) counts[l.state] += 1;
      const remainingM = lines.reduce((a, l) => a + l.remaining_m, 0);
      const plannedM = lines.reduce((a, l) => a + l.original.length_m, 0);
      return {
        block,
        remainingM,
        plannedM,
        text: remainingText(remainingM, plannedM),
        counts,
        patches: lines.filter((l) => l.open !== null && plannedLineRole(l.open) === 'patch').length,
      };
    });

  if (options.arrows) features.push(...directionArrowFeatures(features));

  return {
    remainingM: progress.remainingM,
    plannedM: progress.totalPlannedM,
    text: remainingText(progress.remainingM, progress.totalPlannedM),
    blocks: blockRows,
    features,
    notDrawn: missing > 0 ? [`${missing} line(s): no map projection for their block`] : [],
  };
}
